/** Профиль: name, daily goal, placement levels, badges, avatar, robo-cat LED colour, sounds, Gemini key, sign out. */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useAppData, useLoadedData } from '../state/app-data.tsx';
import { signOut, useSession } from '../state/session.tsx';
import { getGeminiKey, setGeminiKey } from '../data/gemini.ts';
import { GEMINI_KEY_URL } from '../core/tutor.ts';
import { DEFAULT_EYE_COLOR, EYE_COLORS, RoboCat } from '../ui/RoboCat.tsx';
import { playFeedback, setFeedbackEnabled, useFeedbackEnabled } from '../ui/feedback.ts';
import { Avatar } from '../ui/Avatar.tsx';
import { BadgeGrid } from '../ui/Badges.tsx';
import { Icon } from '../ui/Icon.tsx';
import { makeAvatar } from '../ui/avatar-image.ts';
import { removeAvatar, uploadAvatar, type EarnedAchievement } from '../data/api.ts';
import {
  cachedAchievements,
  refreshAchievements,
  runAchievementCheck,
  subscribeAchievements,
} from '../data/achievements.ts';
import { badgeGrid } from '../core/achievements.ts';
import {
  detectPushSupport,
  disableReminders,
  enableReminders,
  loadReminder,
  PushError,
  sendTestReminder,
  updateReminderTime,
} from '../data/push.ts';
import { DEFAULT_REMINDER_TIME } from '../core/push.ts';
import { PlacementLevels } from './PlacementScreen.tsx';
import './profile.css';

const GOALS = [20, 30, 50, 80, 120];

/** Short-lived status line: shows a message, then clears it after a few seconds. */
function useFlash(): [string | null, (message: string | null, sticky?: boolean) => void] {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const flash = (next: string | null, sticky = false) => {
    window.clearTimeout(timer.current);
    setMessage(next);
    if (next && !sticky) timer.current = window.setTimeout(() => setMessage(null), 2500);
  };
  return [message, flash];
}

/** The error to show: the real one when online, a connection hint only when offline. */
function errorText(e: unknown, fallback: string): string {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 'Нет связи с интернетом. Попробуйте позже.';
  const message = e instanceof Error ? e.message.trim() : '';
  return message ? `${fallback}: ${message}` : fallback;
}

function NameAndGoal() {
  const { profile } = useLoadedData();
  const { updateProfile } = useAppData();
  const [name, setName] = useState(profile.display_name);
  const [goal, setGoal] = useState(profile.daily_goal_xp);
  const [status, flash] = useFlash();
  const changed = name.trim() !== '' && name.trim() !== profile.display_name;

  useEffect(() => setGoal(profile.daily_goal_xp), [profile.daily_goal_xp]);

  const saveName = async (e: FormEvent) => {
    e.preventDefault();
    if (!changed) return;
    try {
      await updateProfile({ display_name: name.trim() });
      flash('Сохранено');
    } catch (err) {
      flash(errorText(err, 'Не получилось сохранить имя'), true);
    }
  };

  const saveGoal = async (next: number) => {
    const previous = goal;
    setGoal(next);
    try {
      await updateProfile({ daily_goal_xp: next });
      flash(`Цель дня: ${next} XP`);
    } catch (err) {
      setGoal(previous);
      flash(errorText(err, 'Не получилось сохранить цель'), true);
    }
  };

  return (
    <section className="card stack" aria-labelledby="profile-me">
      <h2 id="profile-me" className="t-section">
        Обо мне
      </h2>
      <form className="row profile__name" onSubmit={saveName}>
        <label className="field">
          <span className="field__label">Имя в лиге</span>
          <input className="input" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
        </label>
        <button type="submit" className={`btn ${changed ? 'btn--ink' : 'btn--secondary'}`} disabled={!changed}>
          Сохранить
        </button>
      </form>
      <fieldset className="profile__goal">
        <legend className="field__label">Цель дня (XP)</legend>
        <div className="profile__goals" role="radiogroup" aria-label="Цель дня">
          {GOALS.map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={goal === value}
              className={`chip profile__goal-chip${goal === value ? ' profile__goal-chip--on' : ''}`}
              onClick={() => void saveGoal(value)}
            >
              {value}
            </button>
          ))}
        </div>
      </fieldset>
      {status && (
        <p className="t-meta profile__status" role="status">
          {status}
        </p>
      )}
    </section>
  );
}

function AvatarPicker() {
  const { profile } = useLoadedData();
  const { updateProfile } = useAppData();
  const userId = useSession().session?.user.id ?? '';
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [status, flash] = useFlash();
  const avatarUrl = profile.avatar_url ?? null;

  const pick = async (file: File | undefined) => {
    if (!file || !userId) return;
    setBusy(true);
    flash('Загружаем фото…', true);
    try {
      const image = await makeAvatar(file);
      const url = await uploadAvatar(userId, image);
      await updateProfile({ avatar_url: url });
      flash('Фото обновлено');
    } catch (e) {
      flash(e instanceof Error && e.message.startsWith('Фото') ? e.message : errorText(e, 'Не получилось загрузить фото'), true);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const removePhoto = async () => {
    setBusy(true);
    try {
      await updateProfile({ avatar_url: null });
      await removeAvatar(userId).catch(() => undefined);
      flash('Фото убрано');
    } catch (e) {
      flash(errorText(e, 'Не получилось убрать фото'), true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card stack" aria-labelledby="profile-avatar">
      <h2 id="profile-avatar" className="t-section">
        Аватар в лиге
      </h2>
      <div className="profile__avatar">
        <Avatar url={avatarUrl} eyeColor={profile.settings.eyeColor} name={profile.display_name} size={96} />
        <div className="profile__avatar-actions">
          <button type="button" className="btn btn--secondary" disabled={busy} onClick={() => input.current?.click()}>
            <Icon name="camera" size={20} /> {avatarUrl ? 'Другое фото' : 'Загрузить фото'}
          </button>
          {avatarUrl && (
            <button type="button" className="btn btn--text" disabled={busy} onClick={() => void removePhoto()}>
              Убрать
            </button>
          )}
        </div>
        <input
          ref={input}
          type="file"
          accept="image/*"
          className="visually-hidden"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => void pick(e.target.files?.[0])}
        />
      </div>
      <p className="t-meta">Без фото — робо-кот с вашим цветом глаз.</p>
      {status && (
        <p className="t-meta profile__status" role="status">
          {status}
        </p>
      )}
    </section>
  );
}

function Achievements() {
  const [earned, setEarned] = useState<EarnedAchievement[]>(cachedAchievements);

  useEffect(() => {
    const unsubscribe = subscribeAchievements(setEarned);
    refreshAchievements()
      .then(() => runAchievementCheck())
      .catch(() => undefined);
    return unsubscribe;
  }, []);

  const badges = badgeGrid(earned);
  const count = badges.filter((b) => b.earned).length;
  return (
    <section className="card stack" aria-labelledby="profile-badges">
      <div className="row">
        <h2 id="profile-badges" className="t-section">
          Достижения
        </h2>
        <span className="spacer" />
        <span className="t-meta">
          {count} из {badges.length}
        </span>
      </div>
      <BadgeGrid badges={badges} />
    </section>
  );
}

function EyeColor() {
  const { profile } = useLoadedData();
  const { updateProfile } = useAppData();
  const current = profile.settings.eyeColor ?? DEFAULT_EYE_COLOR;
  return (
    <section className="card stack" aria-labelledby="profile-eyes">
      <div className="row">
        <h2 id="profile-eyes" className="t-section">
          Глаза робо-кота
        </h2>
        <span className="spacer" />
        <RoboCat size={62} followPointer alive />
      </div>
      <div className="profile__swatches" role="radiogroup" aria-label="Цвет глаз">
        {EYE_COLORS.map((c) => (
          <button
            key={c.value}
            type="button"
            role="radio"
            aria-checked={current === c.value}
            aria-label={c.label}
            className={`swatch${c.value.toLowerCase() === '#f1f5f9' ? ' swatch--light' : ''}`}
            style={{ background: c.value }}
            onClick={() => void updateProfile({ settings: { ...profile.settings, eyeColor: c.value } }).catch(() => undefined)}
          />
        ))}
      </div>
    </section>
  );
}

function SoundSwitch() {
  const on = useFeedbackEnabled();
  return (
    <section className="card" aria-labelledby="profile-sound">
      <label className="row profile__switch">
        <span className="stack profile__switch-text">
          <span id="profile-sound" className="t-section">
            Звуки и вибрация
          </span>
          <span className="t-meta">Сигнал на верный и неверный ответ, на этом устройстве.</span>
        </span>
        <input
          type="checkbox"
          role="switch"
          className="switch"
          checked={on}
          onChange={(e) => {
            setFeedbackEnabled(e.target.checked);
            if (e.target.checked) playFeedback('correct');
          }}
        />
      </label>
    </section>
  );
}

function Reminders() {
  const userId = useSession().session?.user.id ?? '';
  const support = useState(detectPushSupport)[0];
  const [enabled, setEnabled] = useState(false);
  const [time, setTime] = useState(DEFAULT_REMINDER_TIME);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!userId || support !== 'ready') return;
    let active = true;
    loadReminder(userId)
      .then((r) => {
        if (!active) return;
        setEnabled(r.enabled);
        setTime(r.time);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [userId, support]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setStatus(null);
    try {
      await action();
    } catch (e) {
      setStatus(e instanceof PushError ? e.message : 'Не получилось — нет связи? Попробуйте ещё раз.');
    } finally {
      setBusy(false);
    }
  };

  const toggle = (on: boolean) =>
    run(async () => {
      if (on) {
        await enableReminders(userId, time);
        setEnabled(true);
        setStatus(`Готово! Напомню в ${time}, если в этот день ещё не было занятий.`);
      } else {
        await disableReminders(userId, time);
        setEnabled(false);
      }
    });

  const changeTime = (value: string) => {
    setTime(value);
    if (enabled && value) void run(() => updateReminderTime(userId, value));
  };

  const test = () =>
    run(async () => {
      const delivered = await sendTestReminder();
      setStatus(delivered > 0 ? 'Тестовое напоминание отправлено.' : 'Не удалось доставить — включите напоминания заново.');
    });

  return (
    <section className="card stack" aria-labelledby="profile-reminders">
      {support === 'ready' ? (
        <>
          <label className="row profile__switch">
            <span className="stack profile__switch-text">
              <span id="profile-reminders" className="t-section">
                Напоминания
              </span>
              <span className="t-meta">
                Раз в день, если вы ещё не занимались. Вечером — ещё одно, если серия дней под угрозой.
              </span>
            </span>
            <input
              type="checkbox"
              role="switch"
              className="switch"
              checked={enabled}
              disabled={busy || !userId}
              onChange={(e) => void toggle(e.target.checked)}
            />
          </label>
          <div className="row">
            <label className="field profile__time">
              <span className="field__label">Время (Астана)</span>
              <input
                className="input"
                type="time"
                step={900}
                value={time}
                disabled={busy}
                onChange={(e) => changeTime(e.target.value)}
              />
            </label>
            <span className="spacer" />
            {enabled && (
              <button type="button" className="btn btn--text" disabled={busy} onClick={() => void test()}>
                Проверить
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <h2 id="profile-reminders" className="t-section">
            Напоминания
          </h2>
          <p className="t-meta">
            {support === 'ios-needs-home-screen'
              ? 'На iPhone напоминания работают только в приложении с экрана «Домой»: в Safari нажмите «Поделиться» → «На экран „Домой“», откройте Сөйле оттуда и включите напоминания здесь (нужен iOS 16.4 или новее).'
              : 'Этот браузер не поддерживает напоминания. Откройте Сөйле в Safari на iPhone (с экрана «Домой»), в Chrome или Edge.'}
          </p>
        </>
      )}
      {status && (
        <p className="t-meta" role="status">
          {status}
        </p>
      )}
    </section>
  );
}

function GeminiKey() {
  const [saved, setSaved] = useState(() => getGeminiKey() !== null);
  const [value, setValue] = useState('');
  const [show, setShow] = useState(false);

  const save = (e: FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;
    setGeminiKey(value);
    setValue('');
    setSaved(true);
  };

  return (
    <section className="card stack" aria-labelledby="profile-ai">
      <h2 id="profile-ai" className="t-section">
        ИИ (Gemini)
      </h2>
      <p className="t-meta">
        Учитель работает на вашем собственном бесплатном ключе Google Gemini. Ключ хранится только на этом устройстве и
        отправляется только в Google.{' '}
        <a href={GEMINI_KEY_URL} target="_blank" rel="noreferrer">
          Получить ключ в Google AI Studio
        </a>
      </p>
      {saved ? (
        <div className="row">
          <span className="chip">Ключ сохранён</span>
          <span className="spacer" />
          <button
            type="button"
            className="btn btn--text"
            onClick={() => {
              setGeminiKey(null);
              setSaved(false);
            }}
          >
            Удалить ключ
          </button>
        </div>
      ) : (
        <form className="stack" onSubmit={save}>
          <p className="t-meta">
            На этом устройстве ключа пока нет. Он хранится только здесь — введите его один раз.
          </p>
          <label className="field">
            <span className="field__label">Ключ API</span>
            <input
              className="input"
              type={show ? 'text' : 'password'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              placeholder="AIza…"
            />
          </label>
          <div className="row">
            <button type="button" className="btn btn--text" onClick={() => setShow((s) => !s)}>
              {show ? 'Скрыть' : 'Показать'}
            </button>
            <span className="spacer" />
            <button type="submit" className="btn btn--ink" disabled={!value.trim()}>
              Сохранить ключ
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

export function ProfileScreen() {
  const email = useSession().session?.user.email ?? '';

  return (
    <div className="screen profile">
      <div className="screen__head">
        <h1 className="t-screen">Профиль</h1>
      </div>
      <div className="stack stack--24">
        <NameAndGoal />
        <PlacementLevels />
        <Achievements />
        <AvatarPicker />
        <EyeColor />
        <Reminders />
        <SoundSwitch />
        <GeminiKey />
        <section className="row profile__account">
          <span className="t-meta">{email}</span>
          <span className="spacer" />
          <button type="button" className="btn btn--text" onClick={() => void signOut()}>
            Выйти
          </button>
        </section>
      </div>
    </div>
  );
}
